import { isEqual } from 'lodash';

/**
 * `card.data` fields that change the backend query / `getCardData` result.
 * Excludes `limit` / `offset` — runtime paging only ({@link TableCardInteractionService}).
 */
const PREVIEW_QUERY_DATA_KEYS = [
	'metrics',
	'dimensions',
	'timeDimension',
	'filters',
	'order',
	'comparison',
] as const;

function normalizeQueryFieldList(value: unknown): unknown {
	if (!Array.isArray(value)) {
		return value;
	}
	return [...value].sort((left, right) => {
		const leftId =
			left && typeof left === 'object' && typeof (left as { id?: unknown }).id === 'string'
				? (left as { id: string }).id
				: '';
		const rightId =
			right &&
			typeof right === 'object' &&
			typeof (right as { id?: unknown }).id === 'string'
				? (right as { id: string }).id
				: '';
		if (leftId && rightId && leftId !== rightId) {
			return leftId.localeCompare(rightId);
		}
		const leftName =
			left &&
			typeof left === 'object' &&
			typeof (left as { name?: unknown }).name === 'string'
				? (left as { name: string }).name
				: '';
		const rightName =
			right &&
			typeof right === 'object' &&
			typeof (right as { name?: unknown }).name === 'string'
				? (right as { name: string }).name
				: '';
		return leftName.localeCompare(rightName);
	});
}

function queryDataSlice(
	data: Record<string, unknown>
): Record<string, unknown> {
	const slice: Record<string, unknown> = {};
	for (const key of PREVIEW_QUERY_DATA_KEYS) {
		if (!(key in data)) {
			continue;
		}
		const value = data[key];
		if (key === 'metrics' || key === 'dimensions') {
			slice[key] = normalizeQueryFieldList(value);
		} else {
			slice[key] = value;
		}
	}
	return slice;
}

/**
 * Compares table/chart query fields for preview refetch.
 * `dimensions` / `metrics` membership matters; array order does not (display
 * order lives in `visualization.columnOrder`).
 */
export function tableCardDataQueryFieldsEqual(
	a: Record<string, unknown> | undefined,
	b: Record<string, unknown> | undefined
): boolean {
	if (!a && !b) {
		return true;
	}
	if (!a || !b) {
		return false;
	}
	return isEqual(queryDataSlice(a), queryDataSlice(b));
}

/**
 * Stable fingerprint of card query configuration for editor preview refetch deduping.
 * Dimension/metric order is ignored — presentation order is `visualization.columnOrder`.
 */
export function previewCardDataQueryFingerprint(
	data: Record<string, unknown> | undefined
): string {
	if (!data) {
		return '';
	}
	return JSON.stringify(queryDataSlice(data));
}

/**
 * Dedupe key for editor `getCardData` preview fetches (date range + metrics/dimensions).
 */
/** Saved filter ids linked on the card (`CardInput.filterIds` / `CardItem.filters`). */
function previewLinkedFilterIdsFingerprint(
	linkedFilterIds: readonly string[] | undefined
): string {
	if (!linkedFilterIds?.length) {
		return '';
	}
	return linkedFilterIds.slice().sort().join(',');
}

export function buildCardPreviewDataFetchKey(
	cardId: string,
	data: Record<string, unknown> | undefined,
	timeDimensionInput: Record<string, unknown>,
	chartType?: string,
	linkedFilterIds?: readonly string[]
): string {
	const typePart = chartType ? `|${chartType}` : '';
	const linkedPart = previewLinkedFilterIdsFingerprint(linkedFilterIds);
	return `${cardId}${typePart}|${JSON.stringify(timeDimensionInput)}|${previewCardDataQueryFingerprint(data)}|${linkedPart}`;
}

type ParsedPreviewFetchKey = {
	header: string;
	timeDimension: string;
	queryFingerprint: string;
	linked: string;
};

/** Parses `buildCardPreviewDataFetchKey` output (chart type segment optional). */
export function parseCardPreviewDataFetchKey(
	key: string
): ParsedPreviewFetchKey | null {
	const segments = key.split('|');
	if (segments.length < 3) {
		return null;
	}
	const hasChartType =
		segments.length >= 4 && !segments[1].trimStart().startsWith('{');
	if (hasChartType) {
		return {
			header: `${segments[0]}|${segments[1]}`,
			timeDimension: segments[2] ?? '',
			queryFingerprint: segments[3] ?? '',
			linked: segments[4] ?? '',
		};
	}
	return {
		header: segments[0] ?? '',
		timeDimension: segments[1] ?? '',
		queryFingerprint: segments[2] ?? '',
		linked: segments[3] ?? '',
	};
}

/**
 * True when query configuration changed (metrics, dimensions, filters, etc.)
 * but the preview date range (`timeDimensionInput`) alone did not.
 */
export function isQueryConfigFetchKeyChange(
	prevKey: string,
	nextKey: string
): boolean {
	const prev = parseCardPreviewDataFetchKey(prevKey);
	const next = parseCardPreviewDataFetchKey(nextKey);
	if (!prev || !next) {
		return prevKey !== nextKey;
	}
	return (
		prev.header !== next.header ||
		prev.queryFingerprint !== next.queryFingerprint ||
		prev.linked !== next.linked
	);
}

/** Parses the `timeDimensionInput` segment from a preview fetch key. */
export function parseFetchKeyTimeDimension(
	fetchKey: string
): Record<string, unknown> | null {
	const parsed = parseCardPreviewDataFetchKey(fetchKey);
	if (!parsed?.timeDimension) {
		return null;
	}
	try {
		return JSON.parse(parsed.timeDimension) as Record<string, unknown>;
	} catch {
		return null;
	}
}

/** True when card query config and linked filters match the fetch key. */
export function fetchKeyMatchesCardQueryInputs(
	fetchKey: string,
	cardData: Record<string, unknown> | undefined,
	linkedFilterIds: readonly string[] | undefined
): boolean {
	const parsed = parseCardPreviewDataFetchKey(fetchKey);
	if (!parsed) {
		return false;
	}
	if (
		parsed.queryFingerprint !== previewCardDataQueryFingerprint(cardData)
	) {
		return false;
	}
	const linked = linkedFilterIds?.length
		? linkedFilterIds.slice().sort().join(',')
		: '';
	return parsed.linked === linked;
}
